import {
  EcommerceAPIApi as EcommerceApi,
  MonthlyAccount,
  type GarageAccountPaymentRequest,
  type UserMergeRequest,
} from '@parkingbase/ecommerce-api-test';
import { PAYMENT_TIMEOUT_MS } from '@/lib/api/payment-timeout';
import { BaseApiService } from './baseApi.service';
import { logger } from '@/lib/logger';

export class UserService extends BaseApiService {
  protected userApi: EcommerceApi;
  protected userAdministrationApi: EcommerceApi;

  constructor() {
    super();
    this.userApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.userAdministrationApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  /**
   * `skipAuth` is load-bearing: unmarked, this call deadlocks against the token
   * refresh it is performing — see TOKEN_EXCHANGE_PATH in client-api. (PB-4648)
   * `options` lets the root layout cap the call during server render (PB-5068).
   */
  async loginUsingRefreshToken(refreshToken: string, options?: import('axios').RawAxiosRequestConfig) {
    const response = await this.userApi.getAccessToken(undefined, undefined, refreshToken, {
      ...options,
      skipAuth: true,
    });
    return response.data;
  }

  async setRefreshTokenOnCookie(refreshToken: string) {
    try {
      return await fetch('/api/auth/set-token', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      logger.error('Failed to set refresh token cookie', error, {
        feature: 'user-service',
        route: 'setRefreshTokenOnCookie',
      });
      throw error;
    }
  }

  async setImpersonationMarker() {
    try {
      return await fetch('/api/auth/impersonation-marker', { method: 'POST' });
    } catch (error) {
      logger.error('Failed to set impersonation marker cookie', error, {
        feature: 'user-service',
        route: 'setImpersonationMarker',
      });
      throw error;
    }
  }

  async clearImpersonationMarker() {
    try {
      return await fetch('/api/auth/impersonation-marker', { method: 'DELETE' });
    } catch (error) {
      logger.error('Failed to clear impersonation marker cookie', error, {
        feature: 'user-service',
        route: 'clearImpersonationMarker',
      });
      throw error;
    }
  }

  /**
   * Revokes the session at the identity server (PB-5476, AC 11).
   *
   * ## Invariants:
   * - Must run BEFORE `deleteRefreshTokenFromCookie()`. Why: the route handler reads the
   *   refresh token from the very cookie that call deletes.
   * - Sends the in-memory access token as the Authorization header. Why: `POST /token/revoke`
   *   is authenticated, and a Next route handler has no browser session of its own to borrow.
   *   The store is imported lazily to avoid a service-to-store import cycle, mirroring how
   *   `client-api` reaches the same store.
   * - Never throws. Why: the only caller is `logout()`, and leaving someone signed in because
   *   identity was unreachable is the worse outcome. The failure is logged, never swallowed
   *   silently.
   */
  async revokeRefreshToken() {
    try {
      const { useAuthStore } = await import('@/stores/user.store');
      const token = useAuthStore.getState().token ?? '';
      await fetch('/api/auth/revoke-token', {
        method: 'POST',
        headers: token ? { Authorization: token } : {},
      });
    } catch (error) {
      logger.error('Failed to revoke refresh token during logout', error, {
        feature: 'user-service',
        route: 'revokeRefreshToken',
      });
    }
  }

  async deleteRefreshTokenFromCookie() {
    try {
      return await fetch('/api/auth/delete-token', {
        method: 'POST',
      });
    } catch (error) {
      logger.error('Failed to delete refresh token cookie', error, {
        feature: 'user-service',
        route: 'deleteRefreshTokenFromCookie',
      });
      throw error;
    }
  }
  async getUserProfile() {
    const response = await this.userApi.getUserProfile();
    return response.data;
  }

  async updateProfile(data: any) {
    const response = await this.userApi.updateUserProfile(data);
    return response.data;
  }

  async getUserAddresses() {
    const response = await this.userApi.getUserAddress();
    return response.data;
  }

  async getUserVehicles() {
    const response = await this.userApi.getUserVehicles();
    return response.data;
  }

  async getGarageAccountByDomain() {
    const response = await this.userApi.getGarageAccountByDomain();
    return response.data;
  }

  async generateOtpForChangeEmailPhone(company: string, phone?: string, email?: string) {
    const response = await this.userApi.generateOtpForChangeEmailPhoneRequest(
      company,
      phone,
      email
    );
    return response.data;
  }

  /**
   * Verifies the OTP for a pending email/phone change and reports whether the new
   * identifier already belongs to another account. Unlike the plain verify, the
   * backend defers applying the change when conflicts are found so the caller can
   * offer a merge (or cancel) — see UserController#verifyOtpWithConflictCheckForUser.
   */
  async verifyOtpForChangeEmailPhoneWithConflictCheck(otp: string) {
    const response = await this.userApi.verifyOtpWithConflictCheckForUser(otp);
    return response.data;
  }

  async mergeProfiles(request: UserMergeRequest) {
    const response = await this.userApi.mergeProfiles(request);
    return response.data;
  }

  async deleteAccount() {
    const response = await this.userApi.deleteEcommerceUser();
    return response.data;
  }

  async getZendeskToken() {
    const response = await this.userApi.getZendeskToken();
    return response.data;
  }

  async getTransactionsForAccount(garageAccountId: string) {
    const response =
      await this.userApi.getGarageAccountsWithInvoicesPaymentsForGarageAccount(garageAccountId);
    return response.data;
  }

  async getSubAccounts(): Promise<Record<string, MonthlyAccount>> {
    const response = await this.userApi.getSubAccounts();
    // Generated type is MonthlyAccount (singular) but actual response is a dict keyed by documentId.
    // See: vm-storybook-ts MonthlyAccountStore.ts:1541-1543
    return response.data as unknown as Record<string, MonthlyAccount>;
  }

  async getInvoicePDF(garageAccountId: string, invoiceDocumentId: string) {
    return this.userApi.getInvoicePDF(garageAccountId, invoiceDocumentId, {
      responseType: 'blob',
    });
  }

  async getPaymentPDF(garageAccountId: string, paymentDocumentId: string) {
    return this.userApi.getPaymentPDF(garageAccountId, paymentDocumentId, {
      responseType: 'blob',
    });
  }

  async deleteGarageAccount(id: string) {
    const response = await this.userApi.deleteGarageAccount(id);
    return response.data;
  }

  async testGarageAccountLink(
    accountNumber: string,
    emailAddress?: string,
    phoneNumber?: string,
    licensePlate?: string,
    companyId?: string
  ) {
    const response = await this.userApi.testGarageAccountLink(
      accountNumber,
      emailAddress,
      phoneNumber,
      licensePlate,
      companyId
    );
    return response.data;
  }

  async linkGarageAccount(
    accountNumber: string,
    emailAddress?: string,
    phoneNumber?: string,
    licensePlate?: string,
    companyId?: string
  ) {
    const response = await this.userApi.linkGarageAccount(
      accountNumber,
      emailAddress,
      phoneNumber,
      licensePlate,
      companyId
    );
    return response.data;
  }

  async linkGarageAccountByAccountId(companyId: string, accountId: string) {
    const response = await this.userApi.linkGarageAccountByAccountId(companyId, accountId);
    return response.data;
  }

  async createPaymentForGarageAccount(
    garageAccountId: string,
    paymentMethodId: string,
    request?: GarageAccountPaymentRequest,
    options?: import('axios').RawAxiosRequestConfig
  ) {
    const response = await this.userApi.createPaymentForGarageAccount(
      garageAccountId,
      paymentMethodId,
      request,
      { ...options, timeout: PAYMENT_TIMEOUT_MS }
    );
    return response.data;
  }

  async sendPasskeySetupEmail() {
    const response = await this.userAdministrationApi.sendEcommercePasskeySetup();
    return response.data;
  }
}
